import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import './BlogArticle.css';

const articles = {
  'who-docuhounds-is-built-for': {
    title: 'Who DocuHounds Is Built For',
    date: 'February 8, 2026',
    image: '/blog/Who_DocuHounds_is_built_for.png',
    content: `Most education tools are designed around an idealized version of college life. A student who lives on campus, has flexible time, and can make school their full-time focus.

That version of college exists. It's just not the reality for most students.

DocuHounds was built for the students whose education has to coexist with real life. Work schedules. Commutes. Family responsibilities. Financial pressure. The kind of students who care deeply about school because the stakes are high and there isn't much margin for error.

We're here for working students. Commuter students. Community college students. First-generation students. Students who are doing everything right and still feel like the system wasn't designed with them in mind.

If school isn't your only responsibility, you're not behind. You're normal.

A majority of students in American higher education don't live in dorms, don't have unlimited time, and don't get to treat school like a lifestyle. They treat it like an opportunity. Something worth protecting. Something they can't afford to mess up.

That's where DocuHounds fits.

Our product is built to reduce friction in academic workflows. It helps students keep track of deadlines, manage classes, and stay organized when life is busy and unpredictable. Not because productivity is trendy, but because missing an assignment or falling behind has real consequences when you're juggling more than just school.

We're not trying to impress anyone with prestige. We're not here to signal that elite students use our product. We're focused on usefulness. Reliability. Clarity. Tools that work when your attention is split and your time is limited.

DocuHounds is for students who take school seriously even when the world around them makes it harder. Students who aren't looking for shortcuts, just systems that don't waste their time.

We believe markets are won by serving real pain clearly. Not by trying to be everything to everyone, but by building something honest for the people who need it most.

If that sounds like you, you're exactly who we built this for.

Welcome to the Pack.`,
    quote: 'We\'re focused on usefulness. Reliability. Clarity. Tools that work when your attention is split and your time is limited.',
    takeaway: 'DocuHounds is purpose-built for working students, commuter students, and anyone managing school alongside real-life responsibilities — reducing friction in academic workflows when time is limited and the stakes are high.'
  },
  'ambassador-program': {
    title: 'The DocuHounds Ambassador Program Is Live',
    date: 'February 2, 2026',
    image: '/blog/DocuHounds_ambassador_program.png',
    content: `Alongside the beta launch, we're officially rolling out the DocuHounds Ambassador Program.

This program is about bringing real students into the process early and building DocuHounds the right way.

Anyone who becomes a DocuHounds ambassador gets free access to DocuHounds for life. No trial period. No expiration. If you help us build this thing, you're in it for the long haul.

Over the coming weeks, we'll also be rolling out our ambassador rewards program, designed to recognize and reward ambassadors for spreading the word, helping onboard new users, and growing the DocuHounds pack on their campuses and in their communities.

This isn't about handing out promo codes and hoping for the best. It's about building something real with people who actually use it.

Why We're Doing This Differently

A lot of companies in this space take the same approach. They build the car first, polish it, market it hard, and only then see how people actually drive it.

We're taking a different approach.

Think of it like Formula 1.

Some teams focus on building the fastest-looking car on paper. We focused on building the base first. The engine, the chassis, and the systems that matter long term.

Now, with the beta live and ambassadors actively using the product, we're getting real student feedback in real environments. That feedback is what lets us iterate quickly and build a better product over time.

Building With Students, Not Around Them

Ambassadors are not an afterthought for us. They are part of how DocuHounds gets better.

As the platform evolves, ambassadors will see frequent updates, new features, and improvements driven by real usage and feedback. DocuHounds will keep getting sharper as more people use it.

If you are interested in joining the DocuHounds Ambassador Program, email julia@docuhounds.com to learn more.

This is how we believe great products get built. With real users involved early and a focus on getting better every week.

We're excited to grow the DocuHounds pack together.`,
    quote: 'Building with students, not around them — ambassadors are part of how DocuHounds gets better.',
    takeaway: 'DocuHounds ambassadors receive lifetime free access and are integral to the product development process, providing real feedback that drives weekly improvements and feature updates.'
  },
  'beta-launch': {
    title: 'DocuHounds Beta Is Live',
    date: 'January 12, 2026',
    image: '/blog/DocuHounds_beta_live.png',
    content: `Today we officially opened DocuHounds to the public.

After spending the last year building, testing, and refining this platform, we're excited to finally launch the DocuHounds beta and put it in the hands of real users.

DocuHounds started at Boston College with a simple belief: students deserve better tools to manage school without adding more noise to their lives. Not another study app built for screenshots. Not something optimized for influencers. A real product that actually helps people stay organized and on top of their work.

Over the past year, Austin and I have spent an enormous amount of time building this platform from the ground up. We focused on getting the foundation right so DocuHounds could scale, evolve, and improve quickly without breaking every time we add something new.

This beta launch is about opening the doors and letting people use what we've been building.

Built With Intention

We did not rush DocuHounds out the door.

Every major feature exists because it solved a real problem we saw students dealing with every day. If something did not clearly save time or reduce stress, it did not make it into the product.

The goal from day one was simple: build something practical, reliable, and actually useful.

Early Access Pricing Locked In

DocuHounds beta is launching with early access pricing.

Anyone who joins as a beta user will keep that pricing permanently. As long as you stay subscribed, your rate stays the same. No surprises later and no forced upgrades down the line.

We want the people who help shape DocuHounds early to benefit from it long term.

Fast Iteration Is the Standard

This beta is not a static release.

We are iterating quickly and shipping updates often. New features, improvements, and refinements will roll out on a weekly or bi-weekly basis. Some changes will be small. Some will be major. All of them will be driven by real usage and feedback.

If you are using DocuHounds consistently, you will notice it getting better over time.

Just Getting Started

Launching the beta is not the end goal. It is the starting point.

We are excited to learn from users, keep improving the platform, and continue building something that genuinely helps students manage their academic lives more effectively.

If you are joining us now, you are early. And we are glad you are here.

Welcome to DocuHounds Beta.`,
    quote: 'Built with intention — every feature solves a real problem students face every day.',
    takeaway: 'DocuHounds beta is now live with locked-in early access pricing. Beta users keep their rate permanently, and the platform will improve weekly through fast iteration based on real student feedback.'
  },
  welcome: {
    title: 'An introduction to DocuHounds',
    date: 'October 16, 2025',
    image: '/blog/welcome.png',
    content: `DocuHounds was founded at Boston College by two driven builders who saw an opportunity to change how students manage their academic lives. One founder came from an engineering background, with six years of experience in utilities and infrastructure as a licensed Professional Engineer. The other spent more than fifteen years in software development, building scalable systems for enterprise clients. Their paths were completely different, but they shared a common vision: that students deserve better tools to organize their time, stay ahead of deadlines, and actually enjoy the college experience.

DocuHounds Student is being built for students first. It is a direct-to-consumer platform designed to simplify academic life through automation and smart organization. The system automatically reads and processes syllabi, creates clean academic dashboards, populates calendars, and keeps everything organized in one place. It gives students back their time, energy, and focus — helping them study smarter and live better.

The company plans to officially release DocuHounds Student in 2026, beginning with a limited launch for college campuses across New England. The focus remains entirely on delivering value directly to students before expanding into institutional or enterprise partnerships.

Built from the ground up by Boston College founders with deep technical and operational experience, DocuHounds represents the next evolution in personal academic management. It combines engineering precision, intelligent design, and a student-centered mission to reshape how people approach their education.

DocuHounds: your trusted companion in student success. 🐾`,
    quote: 'Built from the ground up by Boston College founders with deep technical and operational experience.',
    takeaway: 'DocuHounds is a student-first academic platform that automates syllabus parsing, creates clear dashboards, and helps students regain time and focus.'
  }
};

export default function BlogArticle() {
  const { id } = useParams();
  const article = articles[id];
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  if (!article) {
    return (
      <div className="public-page">
        <main className="container" style={{ padding: '48px 24px' }}>
          <h1>Article not found</h1>
          <p>We couldn't find the article you're looking for.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="public-page blog-article-page">
      <main className="container article-container">
        <div className="article-top">
          <Link to="/blog" className="article-back btn-reset">
            <span className="arrow">←</span>
            <span className="text">Back to Blog</span>
          </Link>
        </div>

        <div className="article-card article-card--elevated">
          <div className="article-inner">
              <article className="article-main">
                <div className="hero-author-card" aria-hidden>
                  <div className="hero-author-wrapper">
                    <img className="hero-author-image" src={article.image} alt="DocuHounds founders" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/logos/blue_docuhounds_text.png'; }} />
                  </div>
                </div>

                <article className="article-main-content">
                <header className="article-header">
                  <h1 className="article-title">{article.title}</h1>
                  <div className="article-meta">{article.date}</div>
                </header>

                <div className="article-content">
                  {article.content.split('\n\n').map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>

              <div className="founder-quote">
                <em>"{article.quote}"</em>
              </div>

              <div className="key-takeaway">
                <strong>Key Takeaway:</strong>
                <p>{article.takeaway}</p>
              </div>

              </article>
            </article>
          </div>
        </div>

        
      </main>
    </div>
  );
}
